import { clone, compact, memoize } from 'lodash';
import './viewportShared';
import { createLogger } from 'shared/services/logger';
import us from './defines';
import {triggerUScopeEvent} from './_uScope';
import LegacyAjaxWrapper from './lib/data/legacy-ajax-wrapper';
import XInfo from './lib/data/xinfo';
import { Rectangle, Size } from './lib/objects/';
import { limitValue, roundToDecimals, roundToNearset, stripLeadingAtSign } from './lib/utility/helpers';
import { UrlBuilder } from './lib/utility/UrlBuilder';
import OverlayLayout from './overLayout';
import { getHiDpiRatio, renderSettings } from './render-settings';

const logger = createLogger('UScope:ViewportBase');

// ________________________________________________________________________________________________
// viewportBase.js
//    This file contains the ViewportBase object that uScope image renderer's will derive from.
//
//    key functionality of viewportBase includes:
//        o issuing of the XINFO request for the specified image, receives & parses response in imageOpen()
//        o creates the following objects for use by derived classes: xinfo, urlBuilder
//
//    All derived objects must overridden the functions specified by 'OVERRIDE_ME_viewportBase'
//
// ________________________________________________________________________________________________

export default function ViewportBase (initState, uScope) {
  // ______________________________________________________
  // Private Vars
  // ______________________________________________________
  const self = this;
  let jVpDivOuter;
  let jVpDiv;

  // ______________________________________________________
  // "VIRTUAL" METHODS
  // ______________________________________________________
  const OVERRIDE_ME_viewportBase = self; // EVERYTHING identified with OVERRIDE_ME_viewportBase must be overridden by derived object
  OVERRIDE_ME_viewportBase.moveAbsolute = function (left, top) {
  };
  OVERRIDE_ME_viewportBase.moveAbsoluteCenter = function (centerX, centerY) {
  };
  OVERRIDE_ME_viewportBase.zoomAbsolute = function (zoom) {
  };
  OVERRIDE_ME_viewportBase.zoomStep = function (amount) {
  };
  OVERRIDE_ME_viewportBase.viewRotate = function (newAngleDeg) {
  };
  OVERRIDE_ME_viewportBase.getViewLeft = function () {
    return 0;
  };
  OVERRIDE_ME_viewportBase.getViewTop = function () {
    return 0;
  };
  OVERRIDE_ME_viewportBase.getViewLeftTop = function () {
    return undefined;
  };
  OVERRIDE_ME_viewportBase.getViewCenter = function () {
    return 0;
  }; // in base coords
  OVERRIDE_ME_viewportBase.getViewCenterX = function () {
    return 0;
  }; // in base coords
  OVERRIDE_ME_viewportBase.getViewCenterY = function () {
    return 0;
  }; // in base coords
  OVERRIDE_ME_viewportBase.getViewWidth = function () {
    return 0;
  };
  OVERRIDE_ME_viewportBase.getViewHeight = function () {
    return 0;
  };
  OVERRIDE_ME_viewportBase.getViewFocus = function () {
    return 0;
  };
  OVERRIDE_ME_viewportBase.getViewAngle = function () {
    return 0;
  };
  OVERRIDE_ME_viewportBase.getViewZoom = function () {
  };
  OVERRIDE_ME_viewportBase.getViewMag = function () {
    return 0;
  };

  // ______________________________________________________
  // IMAGE-LOOKUP RELATED
  // ______________________________________________________
  const VPBASE_IMAGE = self;
  const imageId = initState.imageId; // quick acccess to "@idnumber"
  const uScopeIdx = initState.uScopeIdx; // quick acccess
  VPBASE_IMAGE.rawImageId = Number(stripLeadingAtSign(imageId));
  VPBASE_IMAGE.getImageId = function () {
    // includes '@' sign
    return imageId;
  };
  VPBASE_IMAGE.getImageIdx = function () {
    return uScopeIdx;
  };

  // ______________________________________________________
  // GETTERS
  // ______________________________________________________
  const VPBASE_GET = self;
  const namespace_ = '.' + imageId;
  VPBASE_GET.eventNameSpaceGet = function () {
    return namespace_;
  };

  // ______________________________________________________
  // NAVIGATION MODE RELATED
  // ______________________________________________________
  const VPBASE_NAV = self;
  VPBASE_NAV.navMode = us.eNavModes.nmNone; // navigation mode
  VPBASE_NAV.navModeGet = function () {
    return self.navMode;
  };

  let isChangingNavMode = false; // prevents infinite recursion in navModeHalt (which is an indication of event hell...)
  // This function returns the state of navigation (autopan, drawing, ...) to none.
  VPBASE_NAV.navModeHalt = function () {
    const navMode = self.navModeGet();

    // mouseleave events enter here to cancel drag, etc.
    if (isChangingNavMode || navMode === us.eNavModes.nmNone) {
      return;
    }

    isChangingNavMode = true;

    let navModeChangeHandled = false;
    switch (navMode) {
      case us.eNavModes.nmDragImage:
      case us.eNavModes.nmThumbDragImage:
      case us.eNavModes.nmZoom:
      case us.eNavModes.nmSmoothZoom:
      case us.eNavModes.nmPinchZoom:
        self.setCursor('auto');
        navModeChangeHandled = true;
        break;
      case us.eNavModes.nmRotatingOverlay:
        navModeChangeHandled = true;
        break;
      default:
    }

    if (!navModeChangeHandled) {
      let results = self.events.triggerWithReturn('viewport:stop-navigation', navMode);
      navModeChangeHandled = compact(results).length > 0;
    }

    // TODO: It appears that not all nav modes are exited using the above logic. Review...

    if (navModeChangeHandled) {
      self.navMode = us.eNavModes.nmNone;
      self.events.trigger('viewport:navigation-halted', navMode);
      triggerUScopeEvent(us.evMODECHANGE, {
        imageId: self.getImageId(),
        uScopeIdx: self.getImageIdx(),
        navMode: self.navMode,
        navModePrev: navMode
      });
    }

    isChangingNavMode = false;
  };
  // This function changes the navigation mode.
  VPBASE_NAV.navModeSwitch = (navMode = us.eNavModes.nmNone) => {
    navMode = Number(navMode);
    if (navMode === self.navMode) {
      return;
    }

    // halt current nav move, if any
    if (self.navMode !== us.eNavModes.nmNone) {
      self.navModeHalt();
    }

    let requestHandled = false;

    switch (navMode) {
      /// //////////////////////////////////////////////////new for tuning window
      case us.eNavModes.nmDragImage:
      case us.eNavModes.nmThumbDragImage:
        requestHandled = true;
        break;
      /// //////////////////////////////////////////////////
      case us.eNavModes.nmSmoothZoom:
      case us.eNavModes.nmPinchZoom:
        break;

      case us.eNavModes.nmAutoPan:
        requestHandled = true;
        break;

      case us.eNavModes.nmRasterScan: // rasterscan mode
        requestHandled = true;
        break;

      case us.eNavModes.nmRotatingOverlay:
        break;
      default:
    }

    if (!requestHandled) {
      let results = self.events.triggerWithReturn('viewport:change-navigation', navMode);
      requestHandled = compact(results).length > 0;
    }

    // TODO: requestHandled doesn't appear to be consistently handled. Review...

    if (requestHandled) {
      triggerUScopeEvent(us.evMODECHANGE, {
        imageId: self.getImageId(),
        uScopeIdx: self.getImageIdx(),
        navMode: navMode,
        navModePrev: self.navMode
      });
    }

    self.events.trigger('viewport:navigation-change', navMode);

    self.navMode = navMode;

    isChangingNavMode = false;
  };

  // ______________________________________________________
  // MOUSE RELATED
  // ______________________________________________________
  const VPBASE_MOUSE = self;
  VPBASE_MOUSE.setCursor = function (type) {
    jVpDiv.css('cursor', type);
  };

  // ______________________________________________________
  // PIN FEATURE RELATED
  // ______________________________________________________
  const VPBASE_PINNED = self;
  VPBASE_PINNED.isPinned = function () {
    return self.initState.isPinned;
  };
  VPBASE_PINNED.isPinnedSet = function (bval) {
    self.initState.isPinned = bval;
    self.toolbar.isPinnedSet(bval);
  };

  // ______________________________________________________
  // SAMPLE RELATED
  // tissue sample type, e.g. "patient", "positive" (control), "negative" (control); can be undefined
  // ______________________________________________________
  const VPBASE_SAMPLE = self;
  VPBASE_SAMPLE.sampleTypeSet = function (type) {
    self.initState.sampleType = type;
    // note: a race condition can occur if the image is still initializing when its state is changed
    //        so the exisitence of the toolbar and overlay layout objects are verified
    if (self.toolbar) {
      self.toolbar.sampleTypeSet(type);
    }
    if (self.overLayout) {
      self.overLayout.refresh();
    }
  };
  VPBASE_SAMPLE.sampleTypeGet = function () {
    return self.initState.sampleType;
  };
  VPBASE_SAMPLE.sampleTypeIsControl = function () {
    return (self.sampleTypeGet() !== 'patient');
  };
  VPBASE_SAMPLE.sampleTypeIsPatient = function () {
    return (self.sampleTypeGet() === 'patient');
  };

  // ______________________________________________________
  // STATUS RELATED
  // ______________________________________________________
  const VPBASE_STATUS = self;
  let status = true; // false if a show stopping error was detected
  let vpInitialized_ = false; // init() completed successfully
  VPBASE_STATUS.getStatus = function () {
    return status;
  };
  VPBASE_STATUS.setStatus = function (bstate) {
    status = (bstate === true);
    return status;
  };
  VPBASE_STATUS.setInitialized = function () {
    vpInitialized_ = true;
  };
  VPBASE_STATUS.isInitialized = function () {
    return vpInitialized_;
  };

  // ______________________________________________________
  // VIEW RELATED
  // ______________________________________________________
  const VPBASE_VIEW = self;
  const vpDivDims = new Size(0, 0); // outer div dimensions, maintained by viewResize()
  VPBASE_VIEW.vpDivGet = function (outer) { // handy access to this image's viewport div - use it!
    return (outer === 'outer') ? jVpDivOuter : jVpDiv;
  };
  VPBASE_VIEW.focus = function () {
    // outer vp has tabindex, used specifically to enable hot keys
    if (!uScope.isModeConference()) {
      jVpDivOuter.focus();
    }
  };
  VPBASE_VIEW.vpDivDimsGet = function () {
    return vpDivDims;
  };
  VPBASE_VIEW.setImageSelected = function (force = false) {
    if (uScope.getSelImageIdx() !== uScopeIdx || force) {
      self.events.trigger('viewport:selected', uScopeIdx, force);
      self.focus();
    }
  };
  /**
   * This function performs one-time initialization for the overlay visuals.
   *   Note that the tile meter overlay is initialized in imageOpen prior to tile requests.
   *     (cc - the tile meter is deprecated now but was part of the performance plugin)
   */
  VPBASE_VIEW.viewOverlayInit_ = function () {
    if (!self.isInitialized()) {
      self.overLayout = new OverlayLayout(self); // arranges overlay visuals (thumbnail, zoom control, etc.)

      self.events.trigger('viewport:initialize-layout');

      // TODO: Does this belong here?
      self.getImageData(); // with all the priority calls issued, now request image data
    }
  };
  /**
   * This function performs all necessary operations to perform/respond to the viewport resize
   * @param resizeThumbnail - a boolean defining whether the thumbnail must be resized
   * TODO: resizeThumbnail is used to size the thumbnail when the viewport is initialized. This can probably be derived in the thumbnail handler instead
   */
  VPBASE_VIEW.viewResize = function (resizeThumbnail) {
    const jOuterVpParent = jVpDivOuter.parent(); // do not cache: layout change causes parent changes
    const borderWidthX2 = 2 * parseInt(jVpDivOuter.css('border-left-width'), 10);
    const rectDiv = new Rectangle(0, 0, (jOuterVpParent.width() - borderWidthX2), (jOuterVpParent.height() - borderWidthX2));

    if (vpDivDims.w === rectDiv.w && vpDivDims.h === rectDiv.h) { // don't resize if not necessary
      return;
    }

    // note that the positions and dimensions of both the outer and inner vp divs must be set
    jVpDivOuter.css({
      'left': rectDiv.x + 'px',
      'top': rectDiv.y + 'px',
      'width': rectDiv.w + 'px',
      'height': rectDiv.h + 'px'
    });

    // inner vp position is relative so its left,top is always 0,0
    jVpDiv.css({
      left: '0',
      top: '0',
      width: rectDiv.w + 'px',
      height: rectDiv.h + 'px'
    });

    vpDivDims.set(jVpDivOuter.width(), jVpDivOuter.height());

    if (vpDivDims.w < 1 || vpDivDims.h < 1) { // collapsing a view forces a dimension change
      return; // so don't update the hidden view
    }

    if (self.isInitialized()) {
      fitZoomCompute();
      if (self.isFitZoom()) {
        self.zoomAbsolute('Fit'); // peforms image refresh at Fit zoom after updating Fit zoom
      }

      if (uScope.getSelImageIdx() === uScopeIdx) { // if this is the selected image
        uScope.topToolbar.position(self.toolbar.getOffsetRect()); // position the main toolbar
      }

      self.events.trigger('viewport:resize', resizeThumbnail);
      triggerUScopeEvent(us.evVIEWRESIZE, {}); // TODO: replace with local event channel above
    }
  };

  const VPBASE_INFO = self;
  VPBASE_INFO.getXinfoObj = function () {
    return self.xinfo;
  };

  // ______________________________________________________
  // IMAGE STATE RELATED
  // ______________________________________________________

  //    - use these accessors or add new ones here, if they require data from xinfo then initialize them in init()
  let fileName; // name of image file; see getImageData
  VPBASE_IMAGE.getFullURL = function () {
    return self.initState.fullURL;
  };
  VPBASE_IMAGE.getServer = function () {
    return self.initState.server;
  };

  // ______________________________________________________
  // IMAGE RELATED
  // ______________________________________________________
  VPBASE_IMAGE.open = async function () {
    // insure min tile multiplier
    if (us.kTILEMULTIPLIER < 1) {
      us.kTILEMULTIPLIER = 1;
    }
    if (jVpDivOuter.length < 1 || jVpDiv.length < 1) { // insure divs exist
      logger.error('cViewport: viewport div does not exist for imageId: ' + imageId);
      self.setStatus(false);
      return;
    }

    // initialize the url builder
    const urlRoot = self.initState.fullURL ? self.initState.fullURL : (self.initState.server + imageId);
    self.urlBuilder = new UrlBuilder(urlRoot, imageId);

    // Load xInfo
    let xinfo;
    try {
      xinfo = await XInfo.load(self.ajax, imageId);
    } catch (error) {
      self.setStatus(false);

      const imageSet = {'images': [{'imagePath': imageId}]};
      uScope.imageUpdate('add-error', imageSet.images, null, error); // remove the vp object and related divs

      triggerUScopeEvent(us.evAJAXERROR, {jqXHR: error.src, imageId: imageId, request: 'XINFO'});
      triggerUScopeEvent(us.evIMAGEREADY, {jqXHR: error.src, imageId: imageId, status: false});
    }

    if (xinfo) {
      self.xinfo = xinfo;
      self.imageOpen(imageId);
    }
  };

  // ............................................ imageOpen()
  // derived should call this first, upon return call:
  //             self.viewOverlayInit_(); //todo:remove the _ (OR MOVE THIS TO HERE!!!!) all overlays controlled by derived class?????                                // load the overlay visuals
  // vpInitialized_ = true;                                     // indicate that initialization is successful
  // self.setImageSelected(true);                                 // inform uScope that this image is in focus
  // and fire evIMAGEREADY
  // todo: would it be better to call a second base function?
  VPBASE_IMAGE.imageOpen = function (inImageId) {
    if (inImageId !== self.getImageId()) {
      logger.error('viewportBase: imageOpen: passed imageId ' + inImageId + ' is not associated with this viewport whose imageId is ' + self.getImageId());
      return self.setStatus(false);
    }
    self.events.trigger('viewport:before-image-open', self);

    self.viewResize(true); // place and size this viewport's div (size into parent div)

    const backgroundColor = (self.getCaptureType() === us.eAICCaptureType.aic_i_FL) ? '#000' : '#fff';
    jVpDiv.css('background-color', backgroundColor); // set viewport background color in accordance with capture type

    bindEvents_(); // add event handlers
    return self.setStatus(true); // set status successful image open, return status

    // This function adds the viewport base event handlers.
    function bindEvents_ () {
      const namespace = self.eventNameSpaceGet();

      // fit zoom changes when the viewport size is changed
      jVpDiv.on('resize', fitZoomCompute);

      $(us.idHashROOT).on(us.evIMAGESELECTED + namespace, function (e, parms) { // image select event
        // set the selected/unselected (green/gray) border color of the outer viewport
        jVpDivOuter.css({
          'border-color': (parms.uScopeIdx === uScopeIdx) ? '#008000' : '#A6B7C7'
        });

        // the main toolbar moves onto the selected image below the image toolbar
        if (parms.uScopeIdx === uScopeIdx) { // if this is the selected image
          self.focus(); // force focus here for hot keys
          uScope.topToolbar.position(self.toolbar.getOffsetRect()); // position the main toolbar
        }
      });

      $(us.idHashROOT).on(us.evNAVMODEHALT + namespace, function (e, parms) { //
        // if this image is specified or ALL images are specified (parms.uScopeIdx == -1) then halt
        // CC - NOTE this event is only triggered by conferencing, specifically to halt all viewports. TODO: refactor to conferencing.
        if (parms.imageId === '@0' || parms.uScopeIdx === -1 || parms.imageId === imageId || parms.uScopeIdx === uScopeIdx) {
          self.navModeHalt();
        }
      });
    }
  };
  // ............................................ imageClose()
  VPBASE_IMAGE.imageClose = function () {
    bindEventsOff_(); // remove event handlers for viewport

    uScope.setImageNextSelected(uScopeIdx); // inform uScope to select the next image (deselect me)

    self.events.$destroy();

    // ................................closure fns
    //    bindEventsOff_ - This function removes event handlers for viewport by namespace:  "." + imageId
    function bindEventsOff_ () {
      const namespace = self.eventNameSpaceGet();
      $(us.idHashROOT).off(namespace);
    }
  };
  // ................................................................. getImageData
  //  This function issues an ajax call to get the image data.
  // TODO: The sole purpose of this function is to retrieve the file name, which is used in the settings dialog (elsewhere?)
  // TODO: Extract to separate service; make available to wherever it's used
  VPBASE_IMAGE.getImageData = function () {
    let attempts_ = 0;
    if (uScope.isThisImageIdValid(imageId)) { // note this check should not be nec.... doing it to see if strange alert that pops up is caused by it ever
      attempts_++;
      const xml = '<ImageId>' + stripLeadingAtSign(imageId) + '</ImageId>';
      const parms = {'imageId': imageId};
      self.ajax.doDATASVRREQ(cbSuccessFn, cbFailureFn, 'GetImageData', xml, parms);
    } else {
      logger.error('viewportBase: getImageData: not a valid imageId: ' + imageId);
    }

    // ................................closure fns
    // ............................................ cbFailureFn()
    // Because it is necessary for analysis to have the compressedFilename of the image, retry on failure
    function cbFailureFn (jqXHR, textStatus, errorThrown) {
      if (attempts_ < 3) {
        logger.error('getImageData: attempt #' + attempts_ + ' ' + self.ajax.getErrorStr(jqXHR, textStatus, errorThrown, self.getImageId()));
        attempts_++;
        const xml = '<ImageId>' + stripLeadingAtSign(imageId) + '</ImageId>';
        const parms = {'imageId': imageId};
        self.ajax.doDATASVRREQ(cbSuccessFn, cbFailureFn, 'GetImageData', xml, parms);
      } else {
        logger.error('getImageData: surrendering after ' + attempts_ + ' attempts, ' + self.ajax.getErrorStr(jqXHR, self.getImageId()));
      }
    }
    // ............................................ cbSuccessFn()
    //  This function parses the response for the compressed file location.
    function cbSuccessFn (data, textStatus, jqXHR) {
      let responseXML = jqXHR.responseText;
      const idx = responseXML.lastIndexOf('>');
      if ((idx) !== -1) {
        // remove the garbage char on the end of the response
        responseXML = responseXML.slice(0, idx + 1);
      }

      const x2js = new X2JS();
      const jsonObj_ = x2js.xml_str2json(responseXML); // convert input xml to json object
      const code = +jsonObj_.GetImageDataResponse.GetImageDataResult.ASResult;
      if (code !== 0) {
        const message = jsonObj_.GetImageDataResponse.GetImageDataResult.ASMessage;
        logger.error('viewportBase: getImageDataRtn:   msg:' + message + '  code: ' + code);
        return;
      }

      const jsImage = jsonObj_.GetImageDataResponse.ImageData.Image;
      const msgImageId = '@' + jsImage.ImageId;
      if (msgImageId === self.getImageId()) { // insure the ajax return is for this image
        fileName = jsImage.CompressedFileLocation; // e.g. \\aperio-00824\images\21287_Her2.svs
      }
    }
  };
  VPBASE_IMAGE.getFileName = function () {
    return fileName || self.xinfo.filename;
  };
  VPBASE_IMAGE.getCompressionTypeDescription = function () {
    return self.xinfo.comptypedesc;
  };
  VPBASE_IMAGE.getCompQuality = function () {
    return self.xinfo.compquality;
  };
  VPBASE_IMAGE.getImageICCProfile = function () {
    return self.xinfo.iccProfile;
  };
  VPBASE_IMAGE.getScanScopeId = function () {
    return self.xinfo.scanScopeId;
  };
  VPBASE_IMAGE.getImageDate = function () {
    return self.xinfo.date;
  };

  VPBASE_IMAGE.getScanner = function () {
    return self.xinfo.scanner;
  };
  VPBASE_IMAGE.getBaseMag = function () {
    return self.xinfo.apparentMag * getHiDpiRatio() * self.getNormalizationFactor();
  };
  VPBASE_IMAGE.getBaseMPP = function () {
    return self.xinfo.MPP;
  };
  VPBASE_IMAGE.getCaptureType = function () {
    return self.xinfo.capturetype;
  };
  VPBASE_IMAGE.hasALabel = function () {
    return self.xinfo.label.isValid;
  };
  VPBASE_IMAGE.hasAThumbnail = function () {
    return self.xinfo.thumb.isValid;
  };
  VPBASE_IMAGE.getThumbFileDims = memoize(function () {
    // not live thumbnail dims but the defn from xinfo
    return new Size(self.xinfo.thumb.width, self.xinfo.thumb.height);
  });
  VPBASE_IMAGE.getThumbZoom = function () {
    // not live thumbnail zoom but the defn from xinfo
    return self.xinfo.thumb.zoom;
  };
  VPBASE_IMAGE.getTileWidth = memoize(function () {
    return self.xinfo.tileWidth * us.kTILEMULTIPLIER;
  });
  VPBASE_IMAGE.getTileHeight = memoize(function () {
    return self.xinfo.tileHeight * us.kTILEMULTIPLIER;
  });

  // image dimensions related ............................
  VPBASE_IMAGE.getImageWidth = function () {
    return self.xinfo.get_imageWidth();
  };
  VPBASE_IMAGE.getImageHeight = function () {
    return self.xinfo.get_imageHeight();
  };
  VPBASE_IMAGE.getImageDims = memoize(function () {
    return new Size(self.getImageWidth(), self.getImageHeight());
  });

  /**
   * The "Normalization factor" standardizes the appearance of a scanned image on a display device. This is pretty
   * complex; consult the unit tests for proper behavior.
   */
  const MAG_CONSTS = {
    BASELINE_MPP: 0.5,
    MIN_MPP: 0.45,
    MAX_MPP: 0.55,
    BASELINE_APP_MAG: 20,
    BASELINE_SCALE_FACTOR: 10, // appMag * mpp
    MIN_SCALE_FACTOR: 9,
    MAX_SCALE_FACTOR: 11
  };
  function getScaleFactor () {
    let scaleFactor = MAG_CONSTS.BASELINE_SCALE_FACTOR;
    if (renderSettings.enableAppMagNormalization) {
      scaleFactor = self.xinfo.MPP * self.xinfo.apparentMag;
    }
    return scaleFactor;
  }
  function magNeedsNormalization () {
    const sf = getScaleFactor();
    return !(sf >= MAG_CONSTS.MIN_SCALE_FACTOR && sf <= MAG_CONSTS.MAX_SCALE_FACTOR);
  }
  VPBASE_IMAGE.getNormalizationFactor = function () {
    let _baseNormalizationFactor;
    if (!magNeedsNormalization()) {
      _baseNormalizationFactor = 1;
    } else {
      const sf = getScaleFactor();
      _baseNormalizationFactor = MAG_CONSTS.BASELINE_SCALE_FACTOR / sf;
    }
    return _baseNormalizationFactor;
  };
  VPBASE_IMAGE.getMinimumMag = function () {
    return self.convertToMag(self.getMinZoom());
  };
  VPBASE_IMAGE.getMaximumMag = function () {
    const baseMag = self.xinfo.get_baseMag();
    return getHiDpiRatio() * baseMag;
  };
  VPBASE_IMAGE.getMinZoom = () => self.getFitZoom(); // just alias fit zoom. Note that fit zoom is computed using screen pixel data and does not need normalization
  VPBASE_IMAGE.getMaxZoom = () => {
    const pyramid = self.getPyramid();
    return pyramid[0].zoom * this.getNormalizationFactor();
  };

  // todo: it would be good to also allow zoom or mag in and return image dims @ that zoom or mag
  const imageDimInTiles_ = {}; // assoc array of zoom, image dimensions in tiles at that zoom
  VPBASE_IMAGE.getImageDimsInTiles = function (zoom) { // This function returns a reference to a Size of the image dimensions in tiles at the given zoom.
    if (!zoom) {
      zoom = 1;
    }

    if (!imageDimInTiles_[zoom]) { // if dims at this zoom isn't already available, compute it
      const csImageDims = self.getImageDims(); // returns a reference
      const csDims = new Size(csImageDims.w, csImageDims.h);
      csDims.toTileNums(self.getTileWidth(), self.getTileHeight(), zoom);

      imageDimInTiles_[zoom] = new Size(csDims.w, csDims.h);
    }

    return imageDimInTiles_[zoom];
  };
  const imageDimAtZoom_ = {}; // assoc array of zoom, image dimensions at that zoom
  VPBASE_IMAGE.getImageDimsAtZoom = function (zoom = 1) { // This function returns a reference to a Size of the image dimensions at the given zoom.
    if (!imageDimAtZoom_[zoom]) { // if dims at this zoom isn't already available, compute it
      const csImageDims = self.getImageDims(); // returns a reference
      const csDims = new Size(csImageDims.w, csImageDims.h);
      csDims.div(zoom, zoom);
      csDims.toInt();
      imageDimAtZoom_[zoom] = new Size(csDims.w, csDims.h);
    }
    return imageDimAtZoom_[zoom];
  };

  // ______________________________________________________
  // ZOOM RELATED
  // ______________________________________________________
  const VPBASE_ZOOM = self;
  let fitZoom;
  function fitZoomCompute () {
    const csImgDims = self.getImageDims();
    const imageWHRatio = csImgDims.w / csImgDims.h; // compute the image w/h ratio

    const viewWHRatio = vpDivDims.w / vpDivDims.h;

    if (vpDivDims.w < 1 || vpDivDims.h < 1) {
      logger.warn('Error: fitZoomCompute: view dimensions are zero!  Defaulting Fit Zoom but this may cause other issues');
      fitZoom = 2;
    }

    // select dimension to scale by
    let zoom = (imageWHRatio > viewWHRatio) ? parseInt(csImgDims.w / vpDivDims.w, 10) : parseInt(csImgDims.h / vpDivDims.h, 10);
    zoom = limitValue(zoom, 1, us.zoomSpecs.fitZoomMax); // constrain fit zoom factor between 1 and max of fitZoomMax
    zoom = roundToDecimals(zoom); // keep zoom in proper resolution

    fitZoom = zoom;
  }
  VPBASE_ZOOM.getFitZoom = function () {
    if (!fitZoom) {
      fitZoomCompute();
    }
    return fitZoom;
  };
  VPBASE_ZOOM.isFitZoom = function (opt_zoom) { // overridden by vpAp
    if (!opt_zoom) {
      opt_zoom = self.getViewZoom();
    }
    return (opt_zoom === self.getFitZoom());
  };

  // ..................................................... view zoom related
  // todo: reconcile prev zoom with derived
  let prevZoom_ = 4.0;
  VPBASE_ZOOM.getViewPrevZoom = function () {
    return prevZoom_;
  };
  VPBASE_ZOOM.convertToZoom = function (mag) { // convert magnification to zoom
    return roundToDecimals(self.getBaseMag() / mag);
  };

  // ..................................................... mag related
  VPBASE_ZOOM.getFitMag = function () {
    return self.convertToMag(self.getFitZoom());
  };
  VPBASE_ZOOM.convertToMag = function (zoom) { // convert zoom to magnification
    let mag = self.getBaseMag() / zoom;

    if (mag >= 2) {
      // if mag > 2 then round it to integer or 1/2 way between
      mag = roundToHalf(mag);
    } else {
      // otherwise round to tenths
      mag = roundToNearset(mag, 10);
    }
    return mag;
  };
  function convertToRealMag (zoom) {
    let mag = self.xinfo.get_baseMag() / zoom;

    if (mag >= 2) {
      // if mag > 2 then round it to integer or 1/2 way between
      mag = roundToHalf(mag);
    } else {
      // otherwise round to tenths
      mag = roundToNearset(mag, 10);
    }
    return mag;
  }

  // ______________________________________________________
  // PYRAMID RELATED
  // ______________________________________________________
  const VPAP_PYRAMID = self;
  VPAP_PYRAMID.getPyramid = function () {
    const pyramid_ = pyramidInit(); // note that the thumbnail is not on the pyramid
    VPAP_PYRAMID.getPyramid = () => pyramid_;
    return pyramid_;

    // This function creates and returns the pyramid used to select the request zooms.
    //
    //        Note that this pyramid structure is not identical to that of the image file.
    //        Specifically
    //            1) its dimensions in tiles is the tile size used by this viewport
    //
    // a typical pyramid for a 20x scan looks like this
    //    pyramid[0]: zoom: 1,    mag: 20,    csTiles: (111x92)
    //    pyramid[1]: zoom: 4,    mag: 5,        csTiles: (28x23)
    //    pyramid[2]: zoom: 16,    mag: 1.25,    csTiles: (7x6)
    // rendering performance degrades as the user zooms closer to the next pyramid because
    // of the amount of data. to accommodate we inject virtual pyramid levels between the,
    // real ones. The example above will be modified as follows:
    //    pyramid[0]: zoom: 1,    mag: 20,    csTiles: (111x92)
    //    pyramid[1]: zoom: 2,    mag: 10,    csTiles: (56x46)
    //    pyramid[2]: zoom: 4,    mag: 5,     csTiles: (28x23)
    //    pyramid[3]: zoom: 8,    mag: 1.25,  csTiles: (14x12)
    //    pyramid[4]: zoom: 16,   mag: 1.25,  csTiles: (7x6)
    //
    function pyramidInit () {
      const xinfo = self.xinfo;
      const numberOfLevels = xinfo.get_imageLevels();
      // Create an array with n levels
      return new Array(numberOfLevels)
        // Fill it so we can iterate it
        .fill(null)
        // Now create an array containing each level's zoom
        .map((v, ix) => xinfo.get_imageZoom(ix))
        // Finally inject intermediate pyramid levels
        .reduce(({lastZoom, out}, currentZoom) => {
          if (lastZoom) {
            const intermediateZoom = lastZoom * 2;
            if (intermediateZoom < currentZoom) {
              out.push(makePyramidLevel(intermediateZoom, true));
            }
          }
          out.push(makePyramidLevel(currentZoom));
          return {
            lastZoom: currentZoom,
            out
          };
        }, { lastZoom: null, out: [] })
        .out;

      function makePyramidLevel (zoom, isDerived = false) {
        const csTilesComputed = self.getImageDimsInTiles(zoom); // rtns a ref - don't change it
        const mag = self.convertToMag(zoom);
        const realMag = convertToRealMag(zoom);

        return {
          zoom,
          isDerived,
          mag,
          realMag,
          csTiles: new Size(csTilesComputed.w, csTilesComputed.h)
        };
      }
    }
  };

  // ______________________________________________________
  // Constructor
  // ______________________________________________________
  (function construct () {
    self.events = uScope.events.$new();
    self.initState = clone(initState); // passed in initial state, save for closure. todo: consider if initState must be public
    self.ajax = new LegacyAjaxWrapper(uScope, self, imageId);
    // mock toolbar. The toolabr is added by the annotations extension, which violates SRP so should be removed. Mock for now.
    self.toolbar = {
      getOffsetRect () { },
      sampleTypeSet () { },
      isPinnedSet () {}
    };

    const clsIMAGEID = us.classFromImageId(imageId);
    jVpDivOuter = $(us.clsDotOUTERVIEWPORT + '.' + clsIMAGEID); // outer div
    jVpDiv = $(us.clsDotVIEWPORT + '.' + clsIMAGEID); // inner div
    // hide the default context menu
    jVpDivOuter.on('contextmenu', (e) => e.preventDefault());
  }());
}

// This function rounds a float value to the nearest integer or 1/2.
function roundToHalf (value) {
  const ival = parseInt(value, 10);
  const fraction = value - ival;
  if (fraction < 0.25) {
    return ival;
  } else if (fraction >= 0.75) {
    return ival + 1;
  }
  return ival + 0.5;
}



// WEBPACK FOOTER //
// ./src/uScope/js/viewportBase.js